<div class="w-full mx-auto p-5 space-y-5">

  <!-- ============================================================ -->
  <!-- Header                                                       -->
  <!-- ============================================================ -->
  <div class="flex flex-wrap justify-between items-end gap-4">
    <div class="min-w-0">
      <div class="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider">
        <span>Operaciones</span>
        <span class="text-slate-300">/</span>
        <span class="text-slate-700">Auditoría</span>
      </div>
      <h1 class="text-2xl font-semibold text-slate-900 mt-1">Auditoría de Planes de Pago</h1>
      <p class="text-sm text-slate-500 mt-1 max-w-2xl">
        Detecta inscripciones con datos inconsistentes (descuadres, duplicados, recibos huérfanos)
        para que cartera priorice correcciones.
      </p>

      <div *ngIf="summary?.lastScanRunAt"
        class="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span class="inline-flex items-center gap-1.5">
          <svg class="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <ng-container *ngIf="selectedScanRunId == null">Último análisis:</ng-container>
          <ng-container *ngIf="selectedScanRunId != null">Análisis seleccionado:</ng-container>
          <span class="tabular-nums">{{ summary?.lastScanRunAt | date:'dd/MM/yyyy HH:mm' }}</span>
        </span>
        <span class="pill pill-neutral font-mono tabular-nums">{{ summary?.lastScanRunId }}</span>
        <span *ngIf="selectedScanRunId != null" class="pill pill-amber">
          <i class="fas fa-history"></i> Viendo histórico
        </span>
      </div>

      <p *ngIf="!summary?.lastScanRunAt"
        class="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
        <i class="fas fa-info-circle"></i>
        Aún no se ejecutó ningún análisis. Hacé clic en "Ejecutar análisis" para empezar.
      </p>
    </div>

    <div class="flex items-center gap-2 flex-wrap">

      <!-- Histórico de scans -->
      <div *ngIf="scanRuns.length > 0" class="relative">
        <button type="button" class="btn btn-secondary" (click)="toggleScanPicker()">
          <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Histórico
          <span class="pill pill-neutral tabular-nums">{{ scanRuns.length }}</span>
          <svg class="w-3 h-3 transition-transform" [class.rotate-180]="scanPickerOpen"
            fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>

        <div *ngIf="scanPickerOpen" class="history-dropdown">
          <div class="history-head">Histórico de análisis</div>

          <button type="button" class="history-item"
            [class.is-current]="selectedScanRunId == null"
            (click)="selectScanRun(null)">
            <div class="flex items-center justify-between">
              <span class="text-sm font-semibold text-blue-700">→ Más reciente (default)</span>
              <span *ngIf="selectedScanRunId == null" class="pill pill-sky">Actual</span>
            </div>
          </button>

          <button *ngFor="let s of scanRuns" type="button" class="history-item"
            [class.is-current]="selectedScanRunId === s.scanRunId"
            (click)="selectScanRun(s.scanRunId)">
            <div class="flex items-center justify-between mb-1.5">
              <span class="text-sm font-semibold text-slate-900 tabular-nums">
                {{ s.scanRunAt | date:'dd/MM/yyyy HH:mm' }}
              </span>
              <span *ngIf="s.isLatest" class="pill pill-emerald">Más reciente</span>
            </div>
            <div class="flex flex-wrap gap-1.5">
              <span class="pill pill-neutral tabular-nums">{{ s.totalFindings }} hallazgos</span>
              <span *ngIf="s.highCount > 0" class="pill pill-rose tabular-nums">{{ s.highCount }} alta</span>
              <span *ngIf="s.mediumCount > 0" class="pill pill-amber tabular-nums">{{ s.mediumCount }} media</span>
              <span *ngIf="s.lowCount > 0" class="pill pill-emerald tabular-nums">{{ s.lowCount }} baja</span>
              <span class="pill pill-sky tabular-nums">{{ s.affectedEnrollments }} insc.</span>
            </div>
            <div class="history-meta">{{ s.scanRunId }}</div>
          </button>
        </div>
      </div>

      <button type="button" class="btn btn-secondary" (click)="exportExcel()"
        [disabled]="isExporting || !summary?.lastScanRunId"
        title="Descarga un .xlsx con el resumen y todos los hallazgos del análisis actual">
        <svg *ngIf="!isExporting" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <svg *ngIf="isExporting" class="animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        {{ isExporting ? 'Generando…' : 'Excel' }}
      </button>

      <button type="button" class="btn btn-ghost" (click)="refresh()" [disabled]="isLoading">
        <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        Refrescar
      </button>

      <button type="button" class="btn btn-primary" (click)="runScan()" [disabled]="!canStart">
        <svg fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
        </svg>
        {{ startingScan ? 'Iniciando…' : (isRunning ? 'Analizando…' : 'Ejecutar análisis') }}
      </button>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- KPIs                                                         -->
  <!-- ============================================================ -->
  <div *ngIf="summary" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

    <div class="kpi-card kpi-slate">
      <div class="kpi-label">
        <span class="kpi-icon">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
        </span>
        Total hallazgos
      </div>
      <div class="kpi-value">{{ summary.totalFindings }}</div>
      <div class="kpi-hint">Detectados en este análisis</div>
    </div>

    <div class="kpi-card kpi-sky">
      <div class="kpi-label">
        <span class="kpi-icon">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
          </svg>
        </span>
        Inscripciones afectadas
      </div>
      <div class="kpi-value">{{ summary.affectedEnrollments }}</div>
      <div class="kpi-hint">Únicas, con al menos un hallazgo</div>
    </div>

    <button type="button" class="kpi-card kpi-rose kpi-card-button"
      [class.is-active]="selectedSeverity === 'high'"
      (click)="filterBySeverity('high')">
      <div class="kpi-label">
        <span class="kpi-icon">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
          </svg>
        </span>
        Severidad alta
      </div>
      <div class="kpi-value text-rose-700">{{ summary.highSeverityCount }}</div>
      <div class="kpi-hint">Requieren atención inmediata · click para filtrar</div>
    </button>

    <button type="button" class="kpi-card kpi-amber kpi-card-button"
      [class.is-active]="selectedSeverity === 'medium'"
      (click)="filterBySeverity('medium')">
      <div class="kpi-label">
        <span class="kpi-icon">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/>
          </svg>
        </span>
        Severidad media
      </div>
      <div class="kpi-value text-amber-700">{{ summary.mediumSeverityCount }}</div>
      <div class="kpi-hint">Revisar y corregir · click para filtrar</div>
    </button>

  </div>

  <!-- ============================================================ -->
  <!-- Live scan progress (panel cuando hay scan corriendo o terminado) -->
  <!-- ============================================================ -->
  <div *ngIf="currentScan"
    class="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <span *ngIf="currentScan.status === 'Running'" class="pill pill-sky">
            <span class="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse"></span>
            Analizando
          </span>
          <span *ngIf="currentScan.status === 'Completed'" class="pill pill-emerald">
            ✓ Completado
          </span>
          <span *ngIf="currentScan.status === 'Failed'" class="pill pill-rose">
            ⚠ Con errores
          </span>
          <span *ngIf="currentScan.status === 'Cancelled'" class="pill pill-amber">
            ⊘ Cancelado
          </span>
          <span class="text-sm font-semibold text-slate-900 tabular-nums">
            {{ currentScan.items.length }} verificaciones
          </span>
          <span *ngIf="currentScan.status !== 'Running'" class="text-sm text-slate-600 tabular-nums">
            · {{ currentScan.totalFindings }} hallazgo{{ currentScan.totalFindings === 1 ? '' : 's' }}
          </span>
        </div>
        <p class="text-xs text-slate-500 font-mono tabular-nums mt-1">{{ currentScan.scanRunId }}</p>
      </div>
      <button *ngIf="currentScan.status === 'Running'"
        type="button" class="btn btn-danger" (click)="cancelScan()">
        <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
        Cancelar scan
      </button>
      <button *ngIf="currentScan.status !== 'Running'"
        type="button" class="btn btn-ghost btn-sm" (click)="closeScanPanel()" title="Cerrar panel">
        <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
      <div *ngFor="let item of currentScan.items" class="scan-item-card"
        [class.is-pending]="item.status === 'Pending'"
        [class.is-running]="item.status === 'Running'"
        [class.is-clean]="item.status === 'Completed' && (item.findingsCount ?? 0) === 0"
        [class.is-with]="item.status === 'Completed' && (item.findingsCount ?? 0) > 0"
        [class.is-failed]="item.status === 'Failed'"
        [class.is-skipped]="item.status === 'Skipped'">
        <div class="scan-label">{{ item.label }}</div>
        <div class="scan-status">
          <span *ngIf="item.status === 'Running'" class="spinner"></span>
          {{ item.status }}
          <span *ngIf="item.durationMs != null" class="scan-meta">· {{ item.durationMs }}ms</span>
        </div>
        <div *ngIf="item.findingsCount != null" class="scan-count">
          {{ item.findingsCount }} hallazgo{{ item.findingsCount === 1 ? '' : 's' }}
        </div>
        <div *ngIf="item.error" class="scan-error truncate" [title]="item.error">⚠ {{ item.error }}</div>
      </div>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- Breakdown por tipo                                           -->
  <!-- ============================================================ -->
  <div *ngIf="summary && summary.byType.length > 0"
    class="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-base font-semibold text-slate-900">Hallazgos por tipo</h2>
        <p class="text-xs text-slate-500 mt-0.5">Click en una tarjeta para filtrar la tabla.</p>
      </div>
      <button *ngIf="selectedFindingType" type="button" class="btn btn-ghost btn-sm"
        (click)="filterByType(selectedFindingType)">
        <i class="fas fa-times"></i> Quitar filtro
      </button>
    </div>
    <div class="grid gap-2.5 md:grid-cols-2 lg:grid-cols-3">
      <button *ngFor="let b of summary.byType; trackBy: trackByType" type="button"
        class="finding-type-card"
        [class.is-active]="selectedFindingType === b.findingType"
        (click)="filterByType(b.findingType)">
        <div class="ft-header">
          <span class="ft-title">
            <span class="w-2 h-2 rounded-full" [ngClass]="severityDot(b.severity)"></span>
            {{ typeLabel(b.findingType) }}
          </span>
          <span class="ft-count tabular-nums">{{ b.count }}</span>
        </div>
        <div class="ft-meta tabular-nums">
          {{ b.affectedEnrollments }} inscripción{{ b.affectedEnrollments === 1 ? '' : 'es' }} afectada{{ b.affectedEnrollments === 1 ? '' : 's' }}
        </div>
        <div *ngIf="b.totalAmountDelta !== 0" class="ft-delta"
          [class.text-rose-700]="b.totalAmountDelta > 0"
          [class.text-emerald-700]="b.totalAmountDelta < 0">
          Δ {{ b.totalAmountDelta | currency:'COP':'symbol-narrow':'1.0-0' }}
        </div>
      </button>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- Toolbar de filtros + Tabla de hallazgos                      -->
  <!-- ============================================================ -->
  <div class="filters-toolbar">
    <div class="grid grid-cols-12 gap-3">

      <div class="col-span-12 md:col-span-4">
        <div class="relative">
          <svg class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input type="number" [(ngModel)]="searchEnrollmentId" (ngModelChange)="applyFilters()"
            placeholder="Buscar por ID de inscripción..."
            class="w-full pl-9 pr-3 h-[38px] border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-slate-400 tabular-nums">
        </div>
      </div>

      <div class="col-span-12 md:col-span-3">
        <select [(ngModel)]="selectedEnrollmentType" (ngModelChange)="applyFilters()"
          class="w-full h-[38px] px-3 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option [ngValue]="null">Todos los tipos de inscripción</option>
          <option value="individual">Individual</option>
          <option value="corporate-company">Corporativo · Empresa</option>
          <option value="corporate-participant">Corporativo · Participante</option>
        </select>
      </div>

      <div class="col-span-12 md:col-span-5 flex items-center gap-2 flex-wrap">
        <span class="text-xs font-medium text-slate-500 uppercase tracking-wider">Severidad</span>
        <button type="button" class="sev-chip sev-high"
          [class.is-active]="selectedSeverity === 'high'"
          [class.is-inactive]="selectedSeverity && selectedSeverity !== 'high'"
          (click)="filterBySeverity('high')">
          <span class="dot"></span> Alta
        </button>
        <button type="button" class="sev-chip sev-medium"
          [class.is-active]="selectedSeverity === 'medium'"
          [class.is-inactive]="selectedSeverity && selectedSeverity !== 'medium'"
          (click)="filterBySeverity('medium')">
          <span class="dot"></span> Media
        </button>
        <button type="button" class="sev-chip sev-low"
          [class.is-active]="selectedSeverity === 'low'"
          [class.is-inactive]="selectedSeverity && selectedSeverity !== 'low'"
          (click)="filterBySeverity('low')">
          <span class="dot"></span> Baja
        </button>

        <button *ngIf="selectedFindingType || selectedSeverity || selectedEnrollmentType || searchEnrollmentId"
          type="button" class="btn btn-ghost btn-sm ml-auto"
          (click)="clearFilters()">
          <i class="fas fa-times"></i> Limpiar filtros
        </button>
      </div>

    </div>
  </div>

  <!-- 🆕 Bulk fix masivo: bar visible cuando filter por findingType bulk-aplicable -->
  <div *ngIf="canBulkFix"
    class="bg-amber-50 border border-amber-300 rounded-xl p-4 flex flex-wrap items-center gap-3 justify-between">
    <div class="text-sm text-slate-800">
      <strong>{{ filteredFindings.length }}</strong> hallazgos de
      <strong>{{ typeLabels[selectedFindingType!] || selectedFindingType }}</strong>
      seleccionados para corrección masiva.
      <span *ngIf="bulkPreview" class="ml-2 text-slate-600">
        ({{ bulkPreview.totalOperations }} operaciones únicas tras dedupe)
      </span>
      <span *ngIf="!bulkScope" class="ml-2 text-rose-600 text-xs">
        ⚠ Filtrá por "Tipo de inscripción" (individual/empresa/participante) para habilitar el bulk
      </span>
    </div>
    <div class="flex items-center gap-2">
      <button type="button" class="btn btn-ghost"
              (click)="loadBulkPreview()" [disabled]="bulkLoading || !bulkScope">
        {{ bulkLoading ? 'Calculando…' : 'Vista previa' }}
      </button>
      <button type="button" class="btn btn-primary"
              (click)="openBulkModal()"
              [disabled]="!bulkPreview || bulkPreview.totalOperations === 0">
        Procesar masivo
        <span *ngIf="bulkPreview"> ({{ bulkPreview.totalOperations }} ops)</span>
      </button>
    </div>
  </div>

  <!-- Tabla de hallazgos -->
  <div class="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <div class="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
      <h2 class="text-base font-semibold text-slate-900">
        Hallazgos
        <span class="ml-2 text-sm font-normal text-slate-500 tabular-nums">
          ({{ filteredFindings.length }} de {{ findings.length }})
        </span>
      </h2>
      <span *ngIf="activeFindingId" class="pill pill-sky">
        <i class="fas fa-bookmark"></i> Marcado #{{ activeFindingId }}
        <button type="button" class="ml-1 text-blue-700 hover:text-blue-900" (click)="clearActiveFinding()" title="Quitar marca">
          <i class="fas fa-times text-[10px]"></i>
        </button>
      </span>
    </div>

    <!-- Empty state -->
    <div *ngIf="!isLoading && filteredFindings.length === 0"
      class="border-t border-slate-100 px-5 py-12 text-center">
      <div class="mx-auto w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <p class="text-sm font-medium text-slate-700">
        <ng-container *ngIf="findings.length === 0">No hay hallazgos. Probá ejecutando un análisis.</ng-container>
        <ng-container *ngIf="findings.length > 0">Ningún hallazgo coincide con los filtros aplicados.</ng-container>
      </p>
      <p *ngIf="findings.length > 0" class="text-xs text-slate-500 mt-1">Ajustá los filtros o limpiá la selección.</p>
    </div>

    <div *ngIf="!isLoading && filteredFindings.length > 0" class="overflow-x-auto">
      <table class="enrollment-table">
        <thead>
          <tr>
            <th>Severidad</th>
            <th>Tipo</th>
            <th>Inscripción</th>
            <th>Detalle</th>
            <th class="text-right">Δ Monto</th>
            <th class="text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let f of filteredFindings; trackBy: trackById"
              [class.is-active]="f.id === activeFindingId">
            <td>
              <span class="pill"
                [class.pill-rose]="f.severity === 'high'"
                [class.pill-amber]="f.severity === 'medium'"
                [class.pill-neutral]="f.severity === 'low'">
                <span class="w-1.5 h-1.5 rounded-full" [ngClass]="severityDot(f.severity)"></span>
                {{ severityLabel(f.severity) }}
              </span>
            </td>
            <td>
              <div class="font-medium text-slate-900">{{ typeLabel(f.findingType) }}</div>
              <div class="text-xs text-slate-500 truncate max-w-xs">{{ f.title }}</div>
            </td>
            <td>
              <div class="font-mono text-xs font-semibold text-slate-900 tabular-nums">#{{ f.enrollmentId }}</div>
              <div class="text-xs text-slate-500">{{ enrollmentTypeLabel(f.enrollmentType) }}</div>
            </td>
            <td class="max-w-md">
              <div class="text-xs text-slate-700">{{ f.detail }}</div>
              <div *ngIf="f.affectedInstallmentIds" class="mt-1 text-[10px] text-slate-400">
                Cuotas: <span class="font-mono tabular-nums">{{ f.affectedInstallmentIds }}</span>
              </div>
              <div *ngIf="f.affectedReceiptIds" class="text-[10px] text-slate-400">
                Recibos: <span class="font-mono tabular-nums">{{ f.affectedReceiptIds }}</span>
              </div>
              <div *ngIf="f.affectedVersionIds" class="text-[10px] text-slate-400">
                Versiones: <span class="font-mono tabular-nums">{{ f.affectedVersionIds }}</span>
              </div>
            </td>
            <td class="text-right">
              <span *ngIf="f.amountDelta != null" class="font-semibold tabular-nums"
                [class.text-rose-700]="(f.amountDelta || 0) > 0"
                [class.text-emerald-700]="(f.amountDelta || 0) < 0"
                [class.text-slate-400]="f.amountDelta === 0">
                {{ f.amountDelta | currency:'COP':'symbol-narrow':'1.0-0' }}
              </span>
              <span *ngIf="f.amountDelta == null" class="text-slate-300">—</span>
            </td>
            <td class="text-right">
              <div class="inline-flex items-center gap-1 justify-end">
                <button type="button" class="row-action" title="Ver detalle completo del hallazgo"
                  (click)="openDetail(f)">
                  <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                  </svg>
                  <span>Detalle</span>
                </button>
                <span class="action-divider"></span>
                <button type="button" class="row-action row-action--success" (click)="openFixModal(f)">
                  <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>Corregir</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>


<!-- ============================================================ -->
<!-- Modal: Corrección caso por caso                              -->
<!-- ============================================================ -->
<div *ngIf="showFixModal && fixingFinding" class="modal-shell" role="dialog" aria-modal="true"
  (click)="closeFixModal()">
  <div class="modal-panel" (click)="$event.stopPropagation()">

    <div class="modal-head">
      <div class="min-w-0">
        <h2 class="text-lg font-semibold text-slate-900">Corregir hallazgo</h2>
        <p class="text-sm text-slate-500 mt-0.5">
          Inscripción <span class="font-mono tabular-nums">#{{ fixingFinding.enrollmentId }}</span>
          · {{ enrollmentTypeLabel(fixingFinding.enrollmentType) }}
        </p>
      </div>
      <button type="button" class="oc-close" (click)="closeFixModal()" aria-label="Cerrar">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <div class="modal-body">

      <!-- Resumen del hallazgo -->
      <div class="alert-banner"
        [class.alert-rose]="fixingFinding.severity === 'high'"
        [class.alert-amber]="fixingFinding.severity === 'medium'"
        [class.alert-sky]="fixingFinding.severity === 'low'">
        <svg class="alert-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z"/>
        </svg>
        <div class="alert-body">
          <div class="alert-title">{{ typeLabel(fixingFinding.findingType) }}</div>
          <div class="alert-text">{{ fixingFinding.title }}</div>
          <div *ngIf="fixingFinding.detail" class="alert-text italic">{{ fixingFinding.detail }}</div>
          <div *ngIf="fixingFinding.amountDelta != null" class="alert-text tabular-nums">
            Δ Monto: <span class="font-mono font-semibold">{{ fixingFinding.amountDelta | currency:'COP':'symbol-narrow':'1.0-0' }}</span>
          </div>
        </div>
      </div>

      <!-- Selector de acción -->
      <div>
        <label class="form-label">¿Qué acción aplicás?</label>
        <div *ngIf="availableActions.length === 0" class="alert-banner alert-amber">
          <svg class="alert-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z"/>
          </svg>
          <div class="alert-body">
            <div class="alert-text">
              No hay acciones automáticas disponibles para este tipo de hallazgo.
              Editá manualmente desde la pantalla de la inscripción.
            </div>
          </div>
        </div>
        <div class="space-y-2">
          <label *ngFor="let a of availableActions" class="action-option"
            [class.is-selected]="selectedActionCode === a.code">
            <input type="radio" name="fixAction" class="mt-1" [value]="a.code" [(ngModel)]="selectedActionCode">
            <div class="flex-1 min-w-0">
              <div class="ao-title">{{ a.label }}</div>
              <div class="ao-desc">{{ a.description }}</div>
            </div>
          </label>
        </div>
      </div>

      <!-- Inputs específicos por acción -->
      <div *ngIf="isAction.rescueOrphan">
        <label class="form-label">Monto de la cuota nueva (opcional)</label>
        <input type="number" [(ngModel)]="fixNewInstallmentAmount" placeholder="Default: monto del recibo"
          class="form-input tabular-nums" />
        <p class="form-hint">Si lo dejás vacío usa el monto bruto del recibo.</p>
      </div>

      <div *ngIf="isAction.reactivateArchived" class="alert-banner alert-emerald">
        <svg class="alert-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <div class="alert-body">
          <div class="alert-title">¿Qué va a pasar?</div>
          <ul class="alert-text list-disc pl-5 space-y-0.5 mt-1">
            <li>Se crea una <strong>nueva versión</strong> del plan de pagos.</li>
            <li>Todas las cuotas activas actuales se clonan a la versión nueva (con sus números originales).</li>
            <li>La cuota archivada se reactiva en la versión nueva, manteniendo su recibo asociado.</li>
            <li>Si su número de cuota ya está ocupado, se renumera al primer número libre.</li>
          </ul>
        </div>
      </div>

      <div *ngIf="isAction.adjustPaidAmount">
        <label class="form-label">Nuevo AmountToPay (requerido)</label>
        <input type="number" [(ngModel)]="fixNewAmountToPay" placeholder="Ej: 627901"
          class="form-input tabular-nums" />
      </div>

      <div *ngIf="isAction.adjustTotal">
        <label class="form-label">Nuevo TotalAmount de la inscripción (requerido)</label>
        <input type="number" [(ngModel)]="fixNewTotalAmount" placeholder="Ej: 1297526"
          class="form-input tabular-nums" />
        <p *ngIf="suggestedTotalFromDetail() != null" class="form-hint">
          Sugerencia (suma de cuotas activas):
          <button type="button" class="ml-1 text-blue-700 hover:underline tabular-nums"
            (click)="fixNewTotalAmount = suggestedTotalFromDetail()">
            {{ suggestedTotalFromDetail() | currency:'COP':'symbol-narrow':'1.0-0' }}
          </button>
        </p>
      </div>

      <!-- Comentario obligatorio -->
      <div>
        <label class="form-label">Comentario para audit trail <span class="text-rose-600">*</span></label>
        <textarea [(ngModel)]="fixComment" rows="3" maxlength="2000"
          placeholder="Ej: Recuperación de cuota PAGADA #2 que fue mal fusionada con castigo manual del flujo viejo."
          class="form-textarea"></textarea>
        <p class="form-hint tabular-nums">{{ fixComment.length }} / 2000 (mín. 10)</p>
      </div>
    </div>

    <div class="modal-foot">
      <button type="button" class="btn btn-secondary" (click)="closeFixModal()" [disabled]="isApplyingFix">
        Cancelar
      </button>
      <button type="button" class="btn btn-primary"
        [disabled]="isApplyingFix || !selectedActionCode || availableActions.length === 0"
        (click)="confirmFix()">
        <svg *ngIf="isApplyingFix" class="animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        {{ isApplyingFix ? 'Aplicando…' : 'Aplicar corrección' }}
      </button>
    </div>
  </div>
</div>


<!-- ============================================================ -->
<!-- Offcanvas: Detalle del hallazgo                              -->
<!-- ============================================================ -->
<div *ngIf="detailOffcanvas.open && detailOffcanvas.finding"
  class="offcanvas-backdrop" (click)="closeDetail()">
  <div class="offcanvas-panel" (click)="$event.stopPropagation()">

    <div class="oc-head">
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="pill"
            [class.pill-rose]="detailOffcanvas.finding.severity === 'high'"
            [class.pill-amber]="detailOffcanvas.finding.severity === 'medium'"
            [class.pill-neutral]="detailOffcanvas.finding.severity === 'low'">
            <span class="w-1.5 h-1.5 rounded-full" [ngClass]="severityDot(detailOffcanvas.finding.severity)"></span>
            {{ severityLabel(detailOffcanvas.finding.severity) }}
          </span>
          <span class="text-[10px] font-mono text-slate-400 tabular-nums">#{{ detailOffcanvas.finding.id }}</span>
        </div>
        <h2 class="mt-2 text-lg font-semibold text-slate-900">{{ detailOffcanvas.finding.title }}</h2>
        <p class="text-xs text-slate-500 mt-0.5">{{ typeLabel(detailOffcanvas.finding.findingType) }}</p>
      </div>
      <button type="button" class="oc-close" (click)="closeDetail()" aria-label="Cerrar">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <div class="oc-body">

      <!-- Inscripción afectada -->
      <section>
        <div class="oc-section-title">Inscripción afectada</div>
        <div class="oc-card">
          <div class="grid grid-cols-2 gap-3">
            <div class="data-cell">
              <span class="data-label">ID</span>
              <span class="data-value font-mono tabular-nums">#{{ detailOffcanvas.finding.enrollmentId }}</span>
            </div>
            <div class="data-cell">
              <span class="data-label">Tipo</span>
              <span class="data-value">{{ enrollmentTypeLabel(detailOffcanvas.finding.enrollmentType) }}</span>
            </div>
            <div *ngIf="detailOffcanvas.finding.corporateEnrollmentId" class="data-cell">
              <span class="data-label">Acuerdo Corp.</span>
              <span class="data-value font-mono tabular-nums">#{{ detailOffcanvas.finding.corporateEnrollmentId }}</span>
            </div>
            <div *ngIf="detailOffcanvas.finding.participantId" class="data-cell">
              <span class="data-label">Participante</span>
              <span class="data-value font-mono tabular-nums">#{{ detailOffcanvas.finding.participantId }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Descripción del problema -->
      <section>
        <div class="oc-section-title">Descripción del problema</div>
        <div class="alert-banner alert-amber">
          <svg class="alert-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z"/>
          </svg>
          <div class="alert-body">
            <p *ngIf="detailOffcanvas.finding.detail" class="alert-text whitespace-pre-wrap leading-relaxed">{{ detailOffcanvas.finding.detail }}</p>
            <p *ngIf="!detailOffcanvas.finding.detail" class="alert-text italic">Sin descripción adicional.</p>

            <div *ngIf="detailOffcanvas.finding.amountDelta != null" class="mt-3 pt-3 border-t border-amber-200">
              <span class="oc-section-title block">Impacto monetario</span>
              <p class="text-lg font-semibold tabular-nums"
                [class.text-rose-700]="(detailOffcanvas.finding.amountDelta || 0) > 0"
                [class.text-emerald-700]="(detailOffcanvas.finding.amountDelta || 0) < 0"
                [class.text-slate-400]="detailOffcanvas.finding.amountDelta === 0">
                {{ detailOffcanvas.finding.amountDelta | currency:'COP':'symbol-narrow':'1.0-0' }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Cuotas afectadas -->
      <section *ngIf="detailOffcanvas.installmentIds.length > 0">
        <div class="oc-section-title">Cuotas afectadas ({{ detailOffcanvas.installmentIds.length }})</div>
        <div class="oc-list">
          <div *ngFor="let id of detailOffcanvas.installmentIds" class="oc-list-item">
            <div class="flex items-center gap-3 min-w-0">
              <span class="initials-avatar tone-a">
                <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2"/>
                </svg>
              </span>
              <div class="min-w-0">
                <p class="text-[11px] uppercase font-medium text-slate-400 tracking-wider">PaymentPlan</p>
                <p class="font-mono text-sm font-semibold text-slate-900 tabular-nums">#{{ id }}</p>
              </div>
            </div>
            <button type="button" class="row-action row-action--primary" (click)="openHistoryTab()"
              title="Ver esta cuota en el historial del plan de pagos">
              <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
              <span>Historial</span>
            </button>
          </div>
        </div>
        <p class="text-[11px] text-slate-400 mt-1.5">Los links abren el historial completo del plan en una nueva pestaña.</p>
      </section>

      <!-- Recibos afectados -->
      <section *ngIf="detailOffcanvas.receiptIds.length > 0">
        <div class="oc-section-title">Comprobantes afectados ({{ detailOffcanvas.receiptIds.length }})</div>
        <div class="oc-list">
          <div *ngFor="let id of detailOffcanvas.receiptIds" class="oc-list-item">
            <div class="flex items-center gap-3 min-w-0">
              <span class="initials-avatar tone-b">
                <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </span>
              <div class="min-w-0">
                <p class="text-[11px] uppercase font-medium text-slate-400 tracking-wider">IncomeReceipt</p>
                <p class="font-mono text-sm font-semibold text-slate-900 tabular-nums">#{{ id }}</p>
              </div>
            </div>
            <button type="button" class="row-action row-action--success" (click)="openSingleReceiptTab(id)"
              title="Buscar este comprobante en una nueva pestaña">
              <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
              <span>Abrir</span>
            </button>
          </div>
        </div>
      </section>

      <!-- Versiones afectadas -->
      <section *ngIf="detailOffcanvas.versionIds.length > 0">
        <div class="oc-section-title">Versiones de plan afectadas ({{ detailOffcanvas.versionIds.length }})</div>
        <div class="oc-card">
          <div class="flex flex-wrap gap-1.5">
            <span *ngFor="let id of detailOffcanvas.versionIds"
              class="pill pill-violet font-mono tabular-nums">#{{ id }}</span>
          </div>
        </div>
      </section>

    </div>

    <div class="oc-foot">
      <button type="button" class="btn btn-emerald" (click)="openFixFromDetail()">
        <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
        </svg>
        Corregir este hallazgo
      </button>

      <div class="flex gap-2">
        <button type="button" class="btn btn-secondary flex-1"
          (click)="goToEnrollment(detailOffcanvas.finding!)">
          <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
          </svg>
          Ver inscripción
        </button>
        <button type="button" class="btn btn-secondary flex-1"
          (click)="goToReceipts(detailOffcanvas.finding!)">
          <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Ver comprobantes
        </button>
      </div>

      <button type="button" class="btn btn-ghost" (click)="openHistoryTab()">
        <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        Ver historial del plan
      </button>
    </div>

  </div>
</div>

<!-- ============================================================ -->
<!-- 🆕 Modal de confirmación de bulk fix (top-level sibling — -->
<!--    NO anidar dentro de otros ngIf para evitar que parent     -->
<!--    container=false oculte este modal).                       -->
<!-- ============================================================ -->
<div *ngIf="showBulkModal"
  class="fixed inset-0 flex items-center justify-center bg-black/50"
  style="z-index: 99999;"
  (click)="$event.target === $event.currentTarget && closeBulkModal()">
  <div class="bg-white rounded-xl shadow-xl max-w-xl w-full mx-4 p-6 space-y-4">
    <h3 class="text-lg font-semibold text-slate-900">Confirmar corrección masiva</h3>
    <p class="text-sm text-slate-600">
      Se ejecutarán <strong>{{ bulkPreview?.totalOperations }} operaciones</strong>
      que resolverán <strong>{{ bulkPreview?.totalFindings }} hallazgos</strong>.
      Esta operación corre en background y se puede cancelar.
    </p>
    <div>
      <label class="block text-sm font-medium text-slate-700 mb-1">
        Motivo (obligatorio, mín. 10 caracteres):
      </label>
      <textarea [(ngModel)]="bulkComment" rows="3"
        class="w-full border border-slate-300 rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Ej: Migración de cuotas legacy a snapshot v0 según pedido de cartera"></textarea>
    </div>
    <div class="flex justify-end gap-2">
      <button type="button" class="btn btn-ghost" (click)="closeBulkModal()">Cancelar</button>
      <button type="button" class="btn btn-primary"
        (click)="confirmBulkFix()"
        [disabled]="!bulkComment || bulkComment.trim().length < 10">
        Iniciar bulk fix
      </button>
    </div>
  </div>
</div>
